// ** React Imports **
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from 'notistack';
import { useDispatch } from "react-redux";
import { useMutation } from "react-query";

// ** API **
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";

// ** Redux **
import { fetchUser } from "src/redux/actions/AuthAction";
import { setNewlyRegisteredBusiness } from "src/redux/slices/AuthSlice";

// ** Components **
import StepProgressIndicator from '../../../components/authentication/StepProgressIndicator';
import LoadingOverlay from '../../../components/authentication/LoadingOverlay';

// ** Styles **
import './RegisterStepReview.css';

/**
 * RegisterStepReview Component (Step 4)
 * 
 * Final review and submit step
 * 
 * Features:
 * - Preview uploaded profile image
 * - Preview uploaded cover image
 * - Delete/change images option
 * - Final submission to create business
 * - Redirect to home after successful completion
 * 
 * @component
 * @param {Object} props
 * @param {Object} props.userData - User data from Step 0
 * @param {Object} props.businessData - Business data from Step 1
 * @param {Object} props.locationData - Location data from Step 2
 * @param {Object} props.imagesData - Images data from Step 3
 * @param {Function} props.onComplete - Callback when flow completed
 */
const RegisterStepReview = ({ userData, businessData, locationData, imagesData, onComplete, onStepClick, onDeleteImage }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Image upload mutation
  const { mutate: uploadImage } = useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append("image", file);
      formData.append("profilePic", 0);
      const res = await axiosInstance.post(
        `${ApiEndPoints.BUSINESS.uploadImage}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return res?.data?.image;
    },
  });

  // Business creation mutation
  const { mutate: createBusiness } = useMutation({
    mutationFn: async (input) => {
      const res = await axiosInstance.post(
        `${ApiEndPoints.BUSINESS.create}`,
        input
      );
      return res.data;
    },
  });

  /**
   * Upload image file
   */
  const uploadImageFile = async (file) => {
    return new Promise((resolve, reject) => {
      uploadImage(file, {
        onSuccess: (data) => {
          resolve(data);
        },
        onError: (error) => {
          reject(error);
        },
      });
    });
  };

  /**
   * Handle final submission
   * Uploads images (if any) and creates business
   */
  const handleFinalSubmit = async () => {
    setIsSubmitting(true);

    try {
      // Upload images first (optional - only if provided)
      let profileImageUrl = null;
      let coverImageUrl = null;

      if (imagesData?.profileImage) {
        profileImageUrl = await uploadImageFile(imagesData.profileImage);
      }

      if (imagesData?.coverImage) {
        coverImageUrl = await uploadImageFile(imagesData.coverImage);
      }

      // Construct business creation payload matching API validation schema
      // API expects: designation, name, businessCategory, email, mobile, 
      // vatNumber, taxRefNumber, registrationNumber, currency, currencySymbol,
      // isMobileAddress, address{longitude, latitude, physicalAddress, businessLocationName},
      // businessLocationUserName, businessLocationName, bannerImage, image, schedule
      const payload = {
        // Required fields
        designation: businessData.designation,
        name: businessData.name,
        businessCategory: businessData.businessCategory,
        email: businessData.email,
        mobile: businessData.mobile,
        
        // Optional fields
        vatNumber: businessData.vatNumber || "",
        taxRefNumber: businessData.taxRefNumber || "",
        registrationNumber: businessData.registrationNumber || "",
        currency: locationData.currency || "",
        currencySymbol: locationData.currencySymbol || "",
        
        // Required: isMobileAddress
        isMobileAddress: false,
        
        // Required: address object with nested fields
        address: {
          longitude: locationData.coordinates.lng,
          latitude: locationData.coordinates.lat,
          physicalAddress: locationData.physicalAddress,
          businessLocationName: locationData.businessLocationName,
        },
        
        // Required: business location fields
        businessLocationUserName: locationData.businessLocationUserName,
        businessLocationName: locationData.businessLocationName,
        
        // Optional: images
        bannerImage: coverImageUrl || "",
        image: profileImageUrl || "",

        // Optional: gallery (URLs from location step presigned uploads)
        photos: Array.isArray(locationData.photos)
          ? locationData.photos
              .map((u) => (typeof u === "string" ? u.trim() : ""))
              .filter(Boolean)
              .slice(0, 30)
          : [],
        
        // Required: schedule object with default 24/7 operation
        schedule: {
          type: "24_hours_all_days",
          days: [],
          hasAdvanceSettings: false,
          specialHours: [],
          timezone: "Asia/Kolkata",
        },
      };

      // Create business
      createBusiness(payload, {
        onSuccess: (data) => {
          enqueueSnackbar("business registration completed successfully!", {
            variant: "success",
            anchorOrigin: { vertical: "top", horizontal: "right" },
            autoHideDuration: 3000,
          });

          // Extract business ID from response
          const businessId = data?.data?.business?._id || data?.business?._id;

          // Set flag for newly registered business requiring subscription
          dispatch(setNewlyRegisteredBusiness({
            isNew: true,
            businessId: businessId
          }));

          // Refresh user data
          dispatch(fetchUser({ storedToken: localStorage.getItem('accessToken') }));

          // Complete onboarding and redirect to home
          setTimeout(() => {
            onComplete();
          }, 1000);
        },
        onError: (error) => {
          console.error("Business creation failed:", error);
          enqueueSnackbar(
            error?.response?.data?.message || "business registration failed. please try again.",
            {
              variant: "error",
              anchorOrigin: { vertical: "top", horizontal: "right" },
              autoHideDuration: 3000,
            }
          );
          setIsSubmitting(false);
        },
      });
    } catch (error) {
      console.error("Submission error:", error);
      enqueueSnackbar("something went wrong. please try again.", {
        variant: "error",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        autoHideDuration: 3000,
      });
      setIsSubmitting(false);
    }
  };

  /**
   * Handle delete profile image
   * Removes profile image and allows re-upload
   */
  const handleDeleteProfile = () => {
    if (onDeleteImage) {
      // Clear profile image from parent state
      onDeleteImage(prevData => ({
        ...prevData,
        profileImage: null,
        profileImagePreview: null,
      }));
      
      enqueueSnackbar("profile image removed. you can upload a new one.", {
        variant: "success",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        autoHideDuration: 2000,
      });
      
      // Navigate back to step 3 to upload new image
      if (onStepClick) {
        onStepClick(3);
      }
    }
  };

  /**
   * Handle delete cover image
   * Removes cover image and allows re-upload
   */
  const handleDeleteCover = () => {
    if (onDeleteImage) {
      // Clear cover image from parent state
      onDeleteImage(prevData => ({
        ...prevData,
        coverImage: null,
        coverImagePreview: null,
      }));
      
      enqueueSnackbar("cover image removed. you can upload a new one.", {
        variant: "success",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        autoHideDuration: 2000,
      });
      
      // Navigate back to step 3 to upload new image
      if (onStepClick) {
        onStepClick(3);
      }
    }
  };

  return (
    <div className="register-step-review">
      <div className="register-step-review__card" style={{ position: 'relative' }}>
        {/* Loading Overlay - Shows during final business creation */}
        <LoadingOverlay 
          isVisible={isSubmitting} 
          type="business"
        />
        
        {/* Progress Indicator */}
        <StepProgressIndicator currentStep={3} totalSteps={3} onStepClick={onStepClick} />

        {/* Title */}
        <h2 className="register-step-review__title">
          {imagesData?.profileImagePreview || imagesData?.coverImagePreview 
            ? 'business profile picture' 
            : 'review & submit'}
        </h2>

        {/* Images Review */}
        <div className="register-step-review__images">
          
          {/* No Images Message */}
          {!imagesData?.profileImagePreview && !imagesData?.coverImagePreview && (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: '#888',
              fontSize: '16px',
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📷</div>
              <p>no images uploaded</p>
              <p style={{ fontSize: '14px', marginTop: '8px' }}>
                you can add images later from your business profile
              </p>
            </div>
          )}
          
          {/* Business Profile */}
          {imagesData?.profileImagePreview && (
            <div className="register-step-review__image-item">
              <label className="register-step-review__label">business profile</label>
              
              <div className="register-step-review__image-preview">
                <img 
                  src={imagesData.profileImagePreview} 
                  alt="profile" 
                  className="register-step-review__image"
                />
                <div className="register-step-review__image-info">
                  <span className="register-step-review__filename">
                    {imagesData.profileImage?.name || 'user-profile.jpg'}
                  </span>
                  <button
                    type="button"
                    className="register-step-review__delete-btn"
                    onClick={handleDeleteProfile}
                  >
                    delete
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Business Cover */}
          {imagesData?.coverImagePreview && (
            <div className="register-step-review__image-item">
              <label className="register-step-review__label">business cover</label>
              
              <div className="register-step-review__image-preview">
                <img 
                  src={imagesData.coverImagePreview} 
                  alt="cover" 
                  className="register-step-review__image register-step-review__image--cover"
                />
                <div className="register-step-review__image-info">
                  <span className="register-step-review__filename">
                    {imagesData.coverImage?.name || 'profile-cover.jpg'}
                  </span>
                  <button
                    type="button"
                    className="register-step-review__delete-btn"
                    onClick={handleDeleteCover}
                  >
                    delete
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Submit Button */}
        <button
          type="button"
          className="register-step-review__submit-btn"
          onClick={handleFinalSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'submitting...' : 'submit'}
        </button>
      </div>
    </div>
  );
};

export default RegisterStepReview;

